import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Stack,
  Fab,
  CircularProgress
} from '@mui/material';
import { Icon } from '@iconify/react';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import editFill from '@iconify/icons-eva/edit-2-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { grey } from '@mui/material/colors';
// context
import { TicketsContext } from '../../../contexts/Tickets';
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
// components
import TicketBatchFormDialog from './form/dialog';
// helper
import { getTimeZoneDate } from '../../../helpers/Util';
// service
import { deleteBatch } from '../../../services/Ticket';
import { updateInventoryMany } from '../../../services/Inventory';

TicketBatchesDialog.propTypes = {
  openTicketBatchesDialog: PropTypes.bool.isRequired,
  setOpenTicketBatchesDialog: PropTypes.func.isRequired,
  ticket: PropTypes.object
};

export default function TicketBatchesDialog({
  openTicketBatchesDialog,
  setOpenTicketBatchesDialog,
  ticket
}) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setTickets } = useContext(TicketsContext);

  const [isLoading, setIsLoading] = useState(false);
  const [focusBatch, setFocusBatch] = useState();
  const [openTicketBatchFormDialog, setOpenTicketBatchFormDialog] = useState(false);

  const handleClose = () => {
    setOpenTicketBatchesDialog(false);
  };

  const handleOpenTicketBatchesFormDialog = (batch) => {
    setFocusBatch(batch);
    setOpenTicketBatchFormDialog(true);
  };

  const handleTicketBatchDelete = async (batch) => {
    if (!window.confirm(`Do you really want to delete this batch: "${batch.name}"?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      await deleteBatch({
        headers,
        eventId: ticket.event._id,
        sectionId: ticket.section._id,
        ticketId: ticket._id,
        batchId: batch._id
      });

      setTickets((tickets) => {
        tickets[ticket.section._id] = tickets[ticket.section._id].map((currentTicket) => {
          if (currentTicket._id === ticket._id) {
            currentTicket.rules = currentTicket.rules.filter(
              (currentBatch) => currentBatch._id !== batch._id
            );
          }
          return currentTicket;
        });

        return tickets;
      });

      // TODO: Atualizar usando kafka (provisório)
      const payloadServices = {
        ticket: {
          _id: ticket._id,
          name: ticket.name,
          reference: ticket.reference,
          limit: ticket.limit,
          price: ticket.price,
          rule: {
            _id: batch._id,
            action: 'delete'
          }
        }
      };

      await updateInventoryMany({ headers, payload: payloadServices });
    } catch (err) {
      console.error(err);
      alert('An error occurred! Please, try again.');
    }

    setIsLoading(false);
  };

  return (
    <>
      <Dialog open={openTicketBatchesDialog} fullWidth scroll="body" maxWidth="sm" sx={{ m: -2 }}>
        <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            Batches - {ticket?.name}
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <Icon icon={closeFill} width="24" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <>
              <DialogContentText>
                Add discount to your ticket based on batch rules
              </DialogContentText>
              {ticket?.rules?.length ? (
                <List sx={{ mt: 2 }}>
                  {ticket.rules.map((rule) => (
                    <Box
                      key={rule._id}
                      sx={{
                        background: grey[100],
                        borderRadius: '20px',
                        mb: 1,
                        p: 1,
                        px: { xs: 0, sm: 1 }
                      }}
                    >
                      <ListItem
                        secondaryAction={
                          <>
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleOpenTicketBatchesFormDialog(rule)}
                            >
                              <Icon icon={editFill} />
                            </IconButton>

                            <IconButton
                              aria-label="delete"
                              onClick={() => handleTicketBatchDelete(rule)}
                            >
                              <Icon icon={trashFill} />
                            </IconButton>
                          </>
                        }
                      >
                        <ListItemText
                          sx={{ width: '55%' }}
                          primary={
                            <>
                              {rule.name}
                              <Typography sx={{ mb: 2 }} color="textSecondary">
                                <small>
                                  Expires in
                                  {` ${getTimeZoneDate({ date: rule.expiresIn })
                                    .tz('Europe/Lisbon')
                                    .format('ddd, DD MMM')}`}
                                </small>
                              </Typography>
                            </>
                          }
                          secondary={`Discount ${rule.discount}% | Limit ${rule.limit}`}
                        />
                      </ListItem>
                    </Box>
                  ))}
                  <Box sx={{ textAlign: 'center' }}>
                    <Fab
                      size="medium"
                      color="primary"
                      aria-label="add"
                      sx={{ flexShrink: 0 }}
                      onClick={handleOpenTicketBatchesFormDialog}
                    >
                      <Icon icon={plusFill} width="24" />
                    </Fab>
                  </Box>
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', p: 5 }}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    No batch yet!
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block' }}
                    >
                      Create your first batch
                    </Typography>
                  </Typography>
                  <Fab
                    size="medium"
                    color="primary"
                    aria-label="add"
                    sx={{ flexShrink: 0 }}
                    onClick={handleOpenTicketBatchesFormDialog}
                  >
                    <Icon icon={plusFill} width="24" />
                  </Fab>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <TicketBatchFormDialog
        openTicketBatchFormDialog={openTicketBatchFormDialog}
        setOpenTicketBatchFormDialog={setOpenTicketBatchFormDialog}
        ticket={ticket}
        batch={focusBatch}
      />
    </>
  );
}
